import { useMemo, useState } from "preact/hooks";
import type { Ctx } from "../app";
import { api, emailTestMode, must, sb, smsConfig } from "../lib/supabase";
import { Badge, Card, ErrorBox, Loading, fmtDate, toast, useAction, useLoad } from "../lib/ui";

// Ready-made messages. {first_name} is filled in per BCBA. Only ever about the BCBA's own credentialing.
const TEMPLATES: Array<{ id: string; label: string; subject: string; body: string }> = [
  { id: "caqh", label: "Please re-attest CAQH", subject: "Please re-attest your CAQH ProView profile",
    body: "Hi {first_name}, please sign in to proview.caqh.org, review your profile and attest, then record the date in the credentialing portal. Only you can attest. Thank you!" },
  { id: "docs", label: "Finish your portal steps", subject: "A few credentialing items are still open",
    body: "Hi {first_name}, a few items are still open in your credentialing portal (about 5 minutes). Please sign in with your email to finish so we can get you in network sooner." },
  { id: "malp", label: "Upload renewed malpractice", subject: "Please upload your renewed malpractice policy",
    body: "Hi {first_name}, please upload the declarations page of your renewed malpractice policy in the credentialing portal. Thank you!" },
  { id: "custom", label: "Write my own", subject: "", body: "" },
];

type Row = { id: string; first_name: string; last_name: string; email: string; phone: string | null; sms_opt_in: boolean; status: string };
type Ready = { provider_id: string; readiness_pct: number; expiring_30d: number; failing_checks: number };
const SEGMENTS: Array<[string, string, (p: Row, r?: Ready) => boolean]> = [
  ["all", "All active BCBAs", (p) => p.status !== "inactive"],
  ["incomplete", "Readiness below 100%", (p, r) => p.status !== "inactive" && (r?.readiness_pct ?? 0) < 100],
  ["expiring", "Something expires in 30 days", (p, r) => p.status !== "inactive" && (r?.expiring_30d ?? 0) > 0],
  ["onboarding", "Still onboarding", (p) => p.status === "onboarding"],
  ["texts", "Opted in to texts", (p) => p.status !== "inactive" && p.sms_opt_in],
];

/** Compose email/text messages to BCBAs, and (admins) see everything the system sent. */
export function MessagesPage({ ctx }: { ctx: Ctx }) {
  const isAdmin = ctx.role === "owner" || ctx.role === "admin";
  const { busy, run } = useAction();
  const [seg, setSeg] = useState("incomplete");
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [tpl, setTpl] = useState(TEMPLATES[0]!);
  const [subject, setSubject] = useState(TEMPLATES[0]!.subject);
  const [body, setBody] = useState(TEMPLATES[0]!.body);
  const [email, setEmail] = useState(true);
  const [sms, setSms] = useState(false);
  const [logKey, setLogKey] = useState(0);
  const { data, error } = useLoad(async () => {
    const [p, r] = await Promise.all([
      sb().from("providers").select("id, first_name, last_name, email, phone, sms_opt_in, status").eq("org_id", ctx.orgId).order("last_name"),
      sb().from("v_provider_readiness").select("provider_id, readiness_pct, expiring_30d, failing_checks").eq("org_id", ctx.orgId),
    ]);
    return { list: must(p) as Row[], ready: new Map((must(r) as Ready[]).map((x) => [x.provider_id, x])) };
  }, [ctx.orgId]);

  const segFn = SEGMENTS.find((s) => s[0] === seg)![2];
  const inSegment = useMemo(() => (data ? data.list.filter((p) => segFn(p, data.ready.get(p.id))) : []), [data, seg]);
  const chosen = picked ?? new Set(inSegment.map((p) => p.id));
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const recipients = inSegment.filter((p) => chosen.has(p.id));
  const textable = recipients.filter((p) => p.sms_opt_in && p.phone).length;
  const smsText = /reply stop/i.test(body) ? body : `${body} Reply STOP to opt out.`;
  const preview = (t: string) => t.replaceAll("{first_name}", recipients[0]?.first_name ?? "Ana");
  const cfg = smsConfig();

  return (
    <>
      <h1>Messages</h1>
      <p class="muted">Reminders to BCBAs about their own credentialing. Automatic nudges (unfinished intake on days 2/5/10, expiring items, CAQH) go out every morning; use this page for anything else. Messages to payers are never sent from here — use the drafted follow-ups on each application.</p>
      {emailTestMode() && <div class="banner">Email test mode is on: emails go to the owner's test inbox.</div>}
      <div class="grid2">
        <Card title="1. Who">
          <div class="chips">{SEGMENTS.map(([id, label]) => (
            <button class={`chip ${seg === id ? "on" : ""}`} onClick={() => { setSeg(id); setPicked(null); }}>{label}</button>
          ))}</div>
          <p class="small"><strong>{recipients.length}</strong> selected · {textable} can receive texts</p>
          <div class="pick-list">{inSegment.map((p) => (
            <label class="check"><input type="checkbox" checked={chosen.has(p.id)} onChange={(e) => {
              const n = new Set(chosen);
              (e.currentTarget as HTMLInputElement).checked ? n.add(p.id) : n.delete(p.id);
              setPicked(n);
            }} />{p.last_name}, {p.first_name} {p.sms_opt_in ? <span class="badge good" title="Opted in to texts">text ✓</span> : null}
              <span class="muted small"> {data.ready.get(p.id)?.readiness_pct ?? 0}%</span></label>
          ))}{!inSegment.length && <p class="muted">Nobody matches.</p>}</div>
        </Card>
        <Card title="2. What">
          <label>Template<select value={tpl.id} onChange={(e) => {
            const t = TEMPLATES.find((x) => x.id === e.currentTarget.value)!;
            setTpl(t); if (t.id !== "custom") { setSubject(t.subject); setBody(t.body); }
          }}>{TEMPLATES.map((t) => <option value={t.id}>{t.label}</option>)}</select></label>
          <label>Email subject<input value={subject} onInput={(e) => setSubject(e.currentTarget.value)} /></label>
          <label>Message <span class="muted small">— {"{first_name}"} is filled in for each person</span>
            <textarea rows={5} value={body} onInput={(e) => setBody(e.currentTarget.value)} /></label>
          <div class="row">
            <label class="check"><input type="checkbox" checked={email} onChange={(e) => setEmail(e.currentTarget.checked)} />Email</label>
            <label class="check"><input type="checkbox" checked={sms} onChange={(e) => setSms(e.currentTarget.checked)} />Text (opted-in only)</label>
            {sms && <span class={`small ${smsText.length > 480 ? "error" : "muted"}`}>{smsText.length}/480 characters</span>}
          </div>
          {sms && cfg.provider === "none" && <p class="small warn-text">Texting isn't connected yet, so texts will be logged as skipped. See Admin → Messaging & alerts.</p>}
          <div class="preview"><p class="muted small">Preview{sms ? " (text)" : ""}:</p><p>{preview(sms && !email ? smsText : body)}</p></div>
          <div class="row-end"><button disabled={busy || !recipients.length || (!email && !sms) || !body.trim()} onClick={() =>
            confirm(`Send to ${recipients.length} BCBA(s)${sms ? ` (${textable} by text)` : ""}?`) && run(async () => {
              const r = await api<{ queued: number; not_opted_in: number }>("/api/app/messages", {
                org_id: ctx.orgId, provider_ids: recipients.map((p) => p.id), channels: [email && "email", sms && "sms"].filter(Boolean), subject, body,
              });
              toast(`${r.queued} message(s) on the way${r.not_opted_in ? ` · ${r.not_opted_in} not opted in to texts` : ""}`);
              setLogKey((k) => k + 1);
            })}>{busy ? "Sending…" : `Send to ${recipients.length}`}</button></div>
        </Card>
      </div>
      {isAdmin && <OutboxLog ctx={ctx} refreshKey={logKey} />}
    </>
  );
}

/** Everything the system queued or sent (reminders, nudges, messages). Admins only (RLS). */
export function OutboxLog({ ctx, refreshKey }: { ctx: Ctx; refreshKey?: number }) {
  const [filter, setFilter] = useState("all");
  const { data, error, reload } = useLoad(async () => {
    let q = sb().from("notifications").select("id, channel, recipient_email, recipient_phone, recipient_kind, subject, status, last_error, created_at, sent_at")
      .eq("org_id", ctx.orgId).order("created_at", { ascending: false }).limit(200);
    if (filter !== "all") q = q.eq("status", filter);
    return must(await q) as any[];
  }, [ctx.orgId, filter, refreshKey]);
  return (
    <Card title="Message log" actions={<>
      <select aria-label="Status" value={filter} onChange={(e) => setFilter(e.currentTarget.value)}>
        {["all", "pending", "sent", "failed", "skipped"].map((s) => <option value={s}>{s}</option>)}</select>
      <button class="ghost small" onClick={reload}>Refresh</button></>}>
      {error ? <ErrorBox error={error} /> : !data ? <Loading /> : !data.length ? <p class="muted">Nothing yet.</p> : (
        <table>
          <thead><tr><th>When</th><th></th><th>To</th><th>Subject</th><th>Status</th></tr></thead>
          <tbody>{data.map((n) => (
            <tr><td class="nowrap">{fmtDate(n.created_at)}</td><td>{n.channel === "sms" ? "💬" : "✉️"}</td>
              <td>{n.recipient_email ?? n.recipient_phone} <span class="muted small">{n.recipient_kind === "member" ? "staff" : "BCBA"}</span></td>
              <td>{n.subject}</td><td><Badge value={n.status} />{n.last_error && <div class="muted small">{n.last_error}</div>}</td></tr>
          ))}</tbody>
        </table>)}
    </Card>
  );
}
